import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import { getIntegrationLogo } from './integration-logos';

interface ActiveConnectionCardProps {
  provider: string;
  onSync: () => void;
  isSyncing: boolean;
  showSync?: boolean;
  webhookSlot?: React.ReactNode;
  deleteSlot?: React.ReactNode;
}

export const ActiveConnectionCard = ({
  provider,
  onSync,
  isSyncing,
  showSync = true,
  webhookSlot,
  deleteSlot,
}: ActiveConnectionCardProps) => {
  const { t } = useTranslation('goals');

  return (
    <div className="rounded-xl border bg-card p-5 transition-colors hover:bg-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border bg-white shadow-sm">
            {getIntegrationLogo(provider)}
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-bold capitalize">{provider}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                {t('integrationsDialog.connected')}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('integrations.manageAndSync')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {webhookSlot}
          {showSync && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onSync}
                  disabled={isSyncing}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isSyncing
                  ? t('integrations.syncing')
                  : t('integrations.syncIssues')}
              </TooltipContent>
            </Tooltip>
          )}
          {deleteSlot}
        </div>
      </div>
    </div>
  );
};
